import { format, getWeek, startOfWeek, endOfWeek, isSameWeek, addDays } from 'date-fns';

export function getWeekNumber(date: Date): number {
  return getWeek(date, { weekStartsOn: 1 }); // Monday start
}

export function formatEventDate(date: Date): string {
  return format(date, 'EEE, MMM d');
}

export function formatEventTime(time: string): string {
  if (!time) return '';
  const trimmed = time.trim();
  const match = trimmed.match(/^(\d{1,2}:\d{2})/);
  if (match) {
    return match[1];
  }

  // Fallback: try to parse using Date for other formats
  const parsed = new Date(`1970-01-01T${trimmed}`);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  return trimmed;
}

export function isEventInPast(eventDate: Date, eventTime: string): boolean {
  const eventDateTime = new Date(eventDate);
  const [hours, minutes] = eventTime.split(':').map(Number);
  eventDateTime.setHours(hours, minutes, 0, 0);
  return eventDateTime < new Date();
}

function getWeekRange(date: Date): { start: Date; end: Date } {
  return {
    start: startOfWeek(date, { weekStartsOn: 1 }),
    end: endOfWeek(date, { weekStartsOn: 1 })
  };
}

export function isNewWeek(currentDate: Date, previousDate: Date | null): boolean {
  if (!previousDate) return true;
  return !isSameWeek(currentDate, previousDate, { weekStartsOn: 1 });
}

function getUpcomingDates(daysCount: number = 14): Date[] {
  const dates: Date[] = [];
  const today = new Date();
  
  for (let i = 0; i < daysCount; i++) {
    dates.push(addDays(today, i));
  }
  
  return dates;
}

export function getCurrentSemester(): { start: Date; end: Date } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-11
  
  // Spring semester: January (0) to June (5)
  if (month >= 0 && month <= 5) {
    return {
      start: new Date(year, 0, 1),  // Jan 1
      end: new Date(year, 5, 30, 23, 59, 59)  // June 30
    };
  }
  
  // Fall semester: August (7) to December (11)
  return {
    start: new Date(year, 7, 1),  // Aug 1
    end: new Date(year, 11, 31, 23, 59, 59)  // Dec 31
  };
}

export function isInCurrentSemester(date: Date): boolean {
  const semester = getCurrentSemester();
  return date >= semester.start && date <= semester.end;
}

export function getRelativeDateLabel(date: Date): string | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const eventDate = new Date(date);
  eventDate.setHours(0, 0, 0, 0);
  
  const diffInMs = today.getTime() - eventDate.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays < 0) return 'Today';
  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays === 2) return '2 days ago';
  if (diffInDays === 3) return '3 days ago';
  if (diffInDays === 4) return '4 days ago';
  if (diffInDays === 5) return '5 days ago';
  if (diffInDays === 6) return '6 days ago';
  if (diffInDays >= 7 && diffInDays <= 13) return 'Past week';
  if (diffInDays >= 14 && diffInDays <= 20) return '2 weeks ago';
  if (diffInDays >= 21 && diffInDays <= 27) return '3 weeks ago';
  
  // For events more than 4 weeks old, return null to use week number
  return null;
}
